// NOTE: Before setting all the types to any, the types became too complex for the type inference to handle
//       and it was causing memory issues. This is a workaround to help with that.
//       The types are still being enforced in the actual components and functions.
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Box, SxProps } from '@mui/material';
import { Fields, FormGroups } from '../types/Form';
import { SetValueConfig, useForm } from 'react-hook-form';
import { Button, ButtonProps } from 'system/atoms';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { FormFields } from './FormFields';
import { findElementsByText } from 'core/utils/findElementsByText';

const formSx: SxProps = {
  display: 'flex',
  flexDirection: 'column',
  gap: '30px'
};

export type FormRef<T extends Yup.AnyObject, K extends keyof T = keyof T> = {
  setValue: (name: K, value: any, options?: SetValueConfig) => void;
  submit: () => void;
  watch: any;
  getValues: () => T;
  reset: () => void;
  // Tells "the user set this" from "this is the default we seeded".
  getDirtyFields: () => string[];
};

export interface FormProps {
  defaultValues?: any;
  schema: Yup.ObjectSchema<any>;
  fields: Fields<any>;
  groups?: FormGroups;
  sx?: SxProps;
  children?: React.ReactNode;
  buttonContainerSx?: SxProps;
  submitButtonProps?: Omit<ButtonProps, 'children'> & {
    label?: string;
  };
  cancelButtonProps?: Omit<ButtonProps, 'children'> & {
    label?: string;
  };
  secondaryButtonProps?: Omit<ButtonProps, 'children'> & {
    label?: string;
  };
  actionButtonProps?: Omit<ButtonProps, 'children'> & {
    label?: string;
  };
  onSecondaryButtonClick?: (data: any) => void;
  onActionButtonClick?: () => void;
  onSubmit?: (data: any) => void;
  onCancel?: () => void;
  context?: any;
  mode?: 'onChange' | 'onBlur' | 'onSubmit' | 'onTouched' | 'all';
}

export const Form = forwardRef<any, FormProps>(
  (
    {
      schema,
      fields,
      groups,
      sx = {},
      buttonContainerSx = {},
      children,
      submitButtonProps = {},
      cancelButtonProps,
      secondaryButtonProps,
      actionButtonProps,
      onSecondaryButtonClick,
      onActionButtonClick,
      onSubmit,
      onCancel,
      defaultValues,
      context,
      mode = 'onChange'
    },
    ref
  ) => {
    const {
      control,
      setValue,
      watch,
      getValues,
      reset,
      formState: { errors, dirtyFields },
      handleSubmit
    } = useForm({
      context,
      resolver: yupResolver(schema),
      defaultValues,
      mode
    });

    useImperativeHandle(ref, () => {
      const refValues: FormRef<any> = {
        setValue: (name, value, options) =>
          setValue(name as string, value, options),
        watch: (props: any) => watch(props),
        submit: () => handleSubmit(handleFormSubmit)(),
        getValues: () => getValues(),
        reset: () => reset(),
        getDirtyFields: () => Object.keys(dirtyFields)
      };
      return refValues;
    });

    useEffect(() => {
      const firstError = Object.entries(errors).reduce<any | null>(
        (acc, entry) => {
          if (acc) return acc;
          return (entry[1] as any).message;
        },
        null
      );
      if (firstError) {
        const element = findElementsByText(firstError as string)[0];
        // A text match can land on a node with no scrollIntoView; throwing here kills the form.
        if (typeof element?.scrollIntoView === 'function') {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }
    }, [errors]);

    const handleFormSubmit = (data: any) => {
      onSubmit?.(data);
    };

    return (
      <Box
        data-testid="form"
        component="form"
        sx={{
          // NOTE: Doing this to help with type inference meomory issues
          ...(formSx as any),
          ...(sx as any)
        }}
        onSubmit={handleSubmit(handleFormSubmit)}
      >
        <FormFields fields={fields} groups={groups} control={control} />
        {children}
        <Box
          sx={{ width: '100%', display: 'flex', gap: 2, ...buttonContainerSx }}
        >
          <Button
            data-testid="form-submit-button"
            type="submit"
            variant="primary"
            {...submitButtonProps}
          >
            {submitButtonProps.label ?? 'Submit'}
          </Button>
          {onSecondaryButtonClick && (
            <Button
              data-testid="form-secondary-button"
              onClick={handleSubmit(onSecondaryButtonClick)}
              variant="secondary"
              {...secondaryButtonProps}
            >
              {secondaryButtonProps?.label ?? 'Secondary'}
            </Button>
          )}
          {onActionButtonClick && (
            <Button
              data-testid="form-action-button"
              onClick={onActionButtonClick}
              variant="secondary"
              {...actionButtonProps}
            >
              {actionButtonProps?.label ?? 'Action'}
            </Button>
          )}
          {onCancel && (
            <Button
              data-testid="form-cancel-button"
              onClick={onCancel}
              variant="tertiary"
              {...cancelButtonProps}
            >
              {cancelButtonProps?.label ?? 'Cancel'}
            </Button>
          )}
        </Box>
      </Box>
    );
  }
);
